// Gradient Force Calculator tool: field metadata + client-side field validation, used
// for instant inline feedback as the user types. The actual math and the authoritative
// (server-side) validation live in the main backend — see src/lib/toolsApi.js
// (gradientForceToolApi) and backend/tools/gradient_force_tool.py.

export const DEFAULT_INPUTS = {
  vehicleMassKg: 1500,
  gradePercent: 10,
  gradeDeg: 5.71,
  gravityMs2: 9.81,
  wheelRadiusM: '',
  speedKmph: '',
};

export const KG_TO_LB = 2.20462262185;
export const LB_TO_KG = 1 / KG_TO_LB;

// Mirrors FIELD_SPECS / GRADE_SPECS / OPTIONAL_SPECS in
// backend/core/lookup/gradient_force_specs.py exactly.
// Mass covers a scooter + rider up to a fully loaded 40-49 t truck; gravity is limited to
// the real spread of Earth's surface gravity.
export const FIELD_SPECS = [
  { key: 'vehicleMassKg', label: 'Vehicle Mass', unit: 'kg', min: 50, max: 60000, strictlyPositive: true },
  { key: 'gravityMs2', label: 'Gravity', unit: 'm/s²', min: 9.78, max: 9.84, strictlyPositive: true },
];

// Grade is signed: positive = uphill, negative = downhill, 0 = flat. 100 % = 45°.
export const GRADE_SPECS = {
  percent: { key: 'gradePercent', label: 'Road Grade', unit: '%', min: -100, max: 100, strictlyPositive: false },
  degree: { key: 'gradeDeg', label: 'Road Grade', unit: '°', min: -45, max: 45, strictlyPositive: false },
};

// Optional: validated only when filled in.
export const OPTIONAL_SPECS = [
  { key: 'wheelRadiusM', label: 'Tyre Rolling Radius', unit: 'm', min: 0.15, max: 0.8, strictlyPositive: true },
  { key: 'speedKmph', label: 'Vehicle Speed', unit: 'km/h', min: 0, max: 200, strictlyPositive: false },
];

// Indicative road grades by setting. Design limits vary by country and design code
// (e.g. IRC in India, AASHTO in the US), so these are typical figures, not rules.
export const TYPICAL_GRADES = [
  { setting: 'Motorway / expressway', grade: '3 – 6 %', maxPercent: 6, note: 'Upper end only in hilly or mountainous terrain.' },
  { setting: 'Main urban and rural roads', grade: 'up to ~8 %', maxPercent: 8, note: 'Longer climbs are usually kept gentler.' },
  { setting: 'Mountain passes / ghat roads', grade: '6 – 12 %', maxPercent: 12, note: 'Sustained climbs; short hairpin sections are steeper.' },
  { setting: 'Local and residential streets', grade: 'up to ~15 %', maxPercent: 15, note: 'Short, steep streets in hilly towns.' },
  { setting: 'Multi-storey car park ramps', grade: '~10 – 20 %', maxPercent: 20, note: 'Short ramps; typical hill-start test range.' },
  { setting: 'Steepest public streets', grade: '~35 %', maxPercent: 35, note: 'Record-holders such as Baldwin Street, Dunedin (NZ).' },
  { setting: 'Off-road / 4×4 climbing specs', grade: '40 – 100 %', maxPercent: 100, note: 'Quoted as gradeability; grip-limited, not power-limited.' },
];

export function validateField(spec, rawValue) {
  if (rawValue === '' || rawValue === null || rawValue === undefined) {
    return 'Required.';
  }
  const value = Number(rawValue);
  if (Number.isNaN(value)) {
    return 'Must be a number.';
  }
  if (spec.strictlyPositive && value <= 0) {
    return 'Must be greater than 0.';
  }
  if (spec.min !== null && value < spec.min) {
    return `Must be ≥ ${spec.min}.`;
  }
  if (spec.max !== null && value > spec.max) {
    return `Must be ≤ ${spec.max}.`;
  }
  return null;
}
